import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Comments from './components/Comments';
import './BlogPost.css';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const contentRef = useRef(null);
  const commentsRef = useRef(null);
  
  // Check if we should show comments from location state
  const shouldShowComments = location.state?.showComments || false;
  const shouldShowForm = location.state?.showForm || false;
  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/wordpress/posts/${slug}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Post not found');
          }
          throw new Error('Failed to fetch post');
        }
        
        const data = await response.json();
        setPost(data);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  // Add target="_blank" to all links in the content
  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.querySelectorAll('a');
      links.forEach(link => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });
    }
  }, [post]);

  // Scroll to comments if shouldShowComments is true
  useEffect(() => {
    if (shouldShowComments && commentsRef.current && post) {
      // Small delay to ensure the comments component has rendered
      const timer = setTimeout(() => {
        commentsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [shouldShowComments, post]);
  

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading post...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '2rem auto',
        padding: '2rem',
        textAlign: 'center'
      }}>
        <div style={{
          color: '#d32f2f',
          backgroundColor: '#ffebee',
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem'
        }}>
          {error}
        </div>
        <button
          onClick={() => navigate('/blog')}
          style={{
            backgroundColor: '#38b44a',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
        >
          ← Back to Blog
        </button>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '2rem 1rem'
    }}>
      <button
        onClick={() => navigate('/blog')}
        style={{
          backgroundColor: 'transparent',
          color: '#38b44a',
          border: 'none',
          padding: '8px 0',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
        onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
        onMouseOut={(e) => e.target.style.textDecoration = 'none'}
      >
        ← Back to Blog
      </button>

      <article style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '3rem'
      }}>
        {post.imageUrl && (
          <div className="blog-post-featured-image">
            <img 
              src={post.imageUrl} 
              alt={post.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div style={{
          padding: '3rem'
        }}>
          <div style={{
            fontSize: '0.875rem',
            color: '#999',
            marginBottom: '1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
          }}>
            {formatDate(post.date)}
          </div>

          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#333',
            marginBottom: '2rem',
            lineHeight: '1.2'
          }}>
            {post.title}
          </h1>

          {post.excerpt && (
            <div
              className="blog-post-content blog-post-excerpt"
              style={{
                fontSize: '1.125rem',
                lineHeight: '1.8',
                color: '#444',
                marginBottom: '2rem'
              }}
              dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
          )}

          <div 
            ref={contentRef}
            className="blog-post-content"
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#444'
            }}
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </div>
      </article>

      {/* Comments Section */}
      <div ref={commentsRef}>
        <Comments 
          contentId={post.id} 
          contentType="blog-post"
          showCommentsByDefault={shouldShowComments}
          showFormByDefault={shouldShowForm}
        />
      </div>
    </div>
  );
}

